import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import LogRocket from 'logrocket';
import { DashboardPage } from '@/features/dashboard/pages/DashboardPage';
import { CounterPage } from '@/features/counter/pages/CounterPage';
// Conditionally import CounterLabPage only in development
// Vite will tree-shake this import out when import.meta.env.DEV is false
import { CounterLabPage } from '@/features/counter/pages/CounterLabPage';

// Conditionally import ReactQueryDevtools only in development
// This ensures it's completely excluded from production builds
// Vite will tree-shake this import out when import.meta.env.DEV is false
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000,
      structuralSharing: true, // Enable structural sharing to prevent unnecessary re-renders
    },
  },
});

// Initialize LogRocket if app ID is provided
console.log('PROD:', import.meta.env.PROD);
console.log('LOGROCKET_APP_ID:', import.meta.env.VITE_LOGROCKET_APP_ID);

if (import.meta.env.PROD && import.meta.env.VITE_LOGROCKET_APP_ID) {
  console.log('LogRocket initializing...');
  LogRocket.init(import.meta.env.VITE_LOGROCKET_APP_ID);
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          token: {
            // Primary colors - matching Fishscope design
            colorPrimary: '#007AFF', // Blue accent
            colorError: '#FF3B30', // Red for stop/error states
            colorSuccess: '#34C759', // Green for success
            colorWarning: '#FF9500', // Orange for warning/loading

            // Background colors
            colorBgContainer: '#FFFFFF', // White cards (Chart, History)
            colorBgElevated: '#4A4A4C', // Dark cards (Video, Status)
            colorBgLayout: '#3B3B3D', // Page background

            // Text colors
            colorText: '#1C1C1E', // Primary text on white
            colorTextSecondary: '#8E8E93', // Secondary/label text
            colorTextTertiary: '#B3B3B3', // Muted text

            // Border
            colorBorder: '#E5E5EA', // Subtle borders
            colorBorderSecondary: '#C7C7CC', // Default borders

            // Border radius - matching screenshot
            borderRadius: 8, // 8px for cards
            borderRadiusLG: 12, // 12px for larger elements
            borderRadiusSM: 6, // 6px for smaller elements

            // Typography - matching screenshot
            fontSize: 13, // Base font size
            fontSizeHeading1: 24, // Large headings
            fontSizeHeading2: 20,
            fontSizeHeading3: 16,
            fontSizeLG: 14,
            fontSizeSM: 12,

            // Spacing
            padding: 20, // Card padding
            paddingLG: 24,
            paddingSM: 16,
            paddingXS: 12,

            // Shadows
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
            boxShadowSecondary: '0 2px 4px 0 rgba(0, 0, 0, 0.15)',
          },
          components: {
            Table: {
              headerBg: '#FFFFFF',
              headerColor: '#8E8E93',
              rowHoverBg: '#F9F9F9',
              borderColor: '#E5E5EA',
              fontSize: 13,
              cellPaddingBlock: 12,
              cellPaddingInline: 16,
            },
            Button: {
              borderRadius: 6,
              controlHeight: 40,
              fontSize: 14,
              fontWeight: 600,
            },
            Card: {
              borderRadiusLG: 8,
              boxShadowTertiary: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            },
          },
        }}
      >
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={<DashboardPage />}
            />
            <Route
              path="/counter/:id"
              element={<CounterPage />}
            />
            {/* Lab route only available in development mode */}
            {import.meta.env.DEV && (
              <Route
                path="/lab"
                element={<CounterLabPage />}
              />
            )}
          </Routes>
        </BrowserRouter>
      </ConfigProvider>
      {/* ReactQueryDevtools is excluded in production builds via Vite's tree-shaking */}
      {/* {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />} */}
    </QueryClientProvider>
  );
}

export default App;
